import React, { useState, useEffect } from "react";
import { MessageCircle, Bell, Mail, Clock, Plus, Calendar } from "lucide-react";
import { useNavigation } from "../contexts/NavigationContext";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { useNotifications } from "../contexts/NotificationContext";
import { useWebSocket } from "../contexts/WebSocketContext";
import { AIPane } from "./AIPane";
import { NotificationsPane } from "./NotificationsPane";
import { MessagingPane } from "./MessagingPane";
import { openMessenger } from "./MessengerManager";

type PaneType = "ai" | "notifications" | "messaging" | null;

interface RightSidebarProps {
  activePane: PaneType;
  setActivePane: (pane: PaneType) => void;
  onNewTimesheet: () => void;
}

interface RightMenuWidget {
  id: string;
  type: "rightMenuAction";
  label: string;
  icon: string;
  action: string;
  description?: string;
  order: number;
  enabled: boolean;
}

const iconMap: Record<string, any> = {
  MessageCircle,
  Mail,
  Bell,
  Clock,
  Calendar,
};

export const RightSidebar: React.FC<RightSidebarProps> = ({
  activePane,
  setActivePane,
  onNewTimesheet,
}) => {
  const { navigate } = useNavigation();
  const { isRTL } = useTranslation();
  const { unreadCount: unreadNotifications, refreshUnreadCount } = useNotifications();
  const { socket } = useWebSocket();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [rightMenuActions, setRightMenuActions] = useState<RightMenuWidget[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    fetchRightMenuSettings();

    // Listen for unread messages updates (from MessagingPane)
    const handleUnreadMessagesUpdate = (event: CustomEvent<number>) => {
      setUnreadMessages(event.detail);
    };

    window.addEventListener(
      "unreadMessagesUpdate" as any,
      handleUnreadMessagesUpdate as EventListener,
    );

    // Fetch initial unread counts
    fetchUnreadMessagesCount();
    refreshUnreadCount();

    return () => {
      window.removeEventListener(
        "unreadMessagesUpdate" as any,
        handleUnreadMessagesUpdate as EventListener,
      );
    };
  }, [refreshUnreadCount]);

  // Listen to Socket.io events for real-time message updates
  // Notification updates are handled by NotificationContext
  useEffect(() => {
    if (!socket) return;

    // Listen for new messages to update unread count in real-time
    // This works even when MessagingPane is closed
    const handleNewMessage = (data: {
      conversationId: string;
      message: {
        _id: string;
        senderId: { _id: string };
        content: string;
        createdAt: string;
      };
      timestamp: string;
    }) => {
      // Fetch updated unread count when a new message arrives
      // This ensures accuracy and handles cases where multiple messages arrive
      fetchUnreadMessagesCount();
    };

    // Set up listeners regardless of connection status
    // Socket.io will queue events if not connected and deliver them when connected
    socket.on("message:new", handleNewMessage);

    return () => {
      socket.off("message:new", handleNewMessage);
    };
  }, [socket]);

  const fetchRightMenuSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/dashboard-settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        const widgets = data.data.widgets || [];
        const rightMenuWidgets = widgets
          .filter(
            (w: RightMenuWidget) => w.type === "rightMenuAction" && w.enabled,
          )
          .sort((a: RightMenuWidget, b: RightMenuWidget) => a.order - b.order);

        setRightMenuActions(rightMenuWidgets);
      }
    } catch (err) {
      console.error("Failed to fetch right menu settings:", err);
      // Fall back to default actions
      setRightMenuActions(getDefaultRightMenuActions());
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadMessagesCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/messages/conversations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        const totalUnread = data.data.reduce(
          (sum: number, conv: any) => sum + (conv.unreadCount || 0),
          0,
        );
        setUnreadMessages(totalUnread);
      }
    } catch (error) {
      console.error("Failed to fetch unread messages count:", error);
    }
  };


  const getDefaultRightMenuActions = (): RightMenuWidget[] => {
    return [
      {
        id: "aiChat",
        type: "rightMenuAction",
        label: "AI Chat",
        icon: "MessageCircle",
        action: "openAIChat",
        description: "Open AI assistant",
        order: 0,
        enabled: true,
      },
      {
        id: "createMessage",
        type: "rightMenuAction",
        label: "Create Message",
        icon: "Mail",
        action: "openMessaging",
        description: "Send a message",
        order: 1,
        enabled: true,
      },
      {
        id: "notifications",
        type: "rightMenuAction",
        label: "Notifications",
        icon: "Bell",
        action: "openNotifications",
        description: "View notifications",
        order: 2,
        enabled: true,
      },
      {
        id: "createTimesheet",
        type: "rightMenuAction",
        label: "Create Timesheet",
        icon: "Clock",
        action: "createTimesheet",
        description: "Add new timesheet",
        order: 3,
        enabled: true,
      },
      {
        id: "createLeave",
        type: "rightMenuAction",
        label: "Create Leave",
        icon: "Calendar",
        action: "createLeave",
        description: "Create leave request",
        order: 4,
        enabled: true,
      },
    ];
  };

  const togglePane = (pane: PaneType) => {
    setActivePane(activePane === pane ? null : pane);
  };

  const handleAction = (action: string) => {
    switch (action) {
      case "openAIChat":
        togglePane("ai");
        break;
      case "openMessaging":
        togglePane("messaging");
        break;
      case "openNotifications":
        togglePane("notifications");
        break;
      case "createTimesheet":
        onNewTimesheet();
        break;
      case "createLeave":
        console.log("[RightSidebar] Dispatching openLeaveForm event");
        window.dispatchEvent(new CustomEvent("openLeaveForm"));
        break;
      default:
        console.log("Unknown action:", action);
    }
  };

  const renderIcon = (iconName: string, className: string = "w-6 h-6") => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className={className} /> : null;
  };

  const getActivePane = (action: string): boolean => {
    switch (action) {
      case "openAIChat":
        return activePane === "ai";
      case "openMessaging":
        return activePane === "messaging";
      case "openNotifications":
        return activePane === "notifications";
      default:
        return false;
    }
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  return (
    <>
      {/* Right Sidebar - Desktop */}
      <aside
        className={`hidden md:flex flex-col bg-white dark:bg-gray-800 ${isRTL ? "border-r" : "border-l"} border-gray-200 dark:border-gray-700 w-16 items-center py-4 space-y-4`}
      >
        {rightMenuActions.map((widget) => (
          <button
            key={widget.id}
            onClick={() => handleAction(widget.action)}
            className={`relative p-3 rounded-lg transition-colors ${
              getActivePane(widget.action)
                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            } ${widget.action === "createTimesheet" ? "bg-accent-600 hover:bg-accent-700 text-white shadow-lg" : ""}`}
            aria-label={widget.label}
            title={widget.label}
          >
            {widget.action === "createTimesheet" ? (
              <div className="relative">
                {renderIcon(widget.icon)}
                <Plus className="w-3 h-3 absolute -top-1 -right-1 bg-white text-accent-600 rounded-full" />
              </div>
            ) : (
              <>
                {renderIcon(widget.icon)}
                {/* Show unread badges for notifications/messages */}
                {widget.action === "openNotifications" &&
                  unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 bg-error-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                      {unreadNotifications > 9 ? "9+" : unreadNotifications}
                    </span>
                  )}
                {widget.action === "openMessaging" && unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 bg-error-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-semibold px-1 shadow-lg z-10">
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </aside>

      {/* Bottom Bar - Mobile */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40">
        <div className="flex justify-around items-center py-2">
          {rightMenuActions.map((widget) => (
            <button
              key={`mobile-${widget.id}`}
              onClick={() => handleAction(widget.action)}
              className={`relative p-3 rounded-lg transition-colors ${
                getActivePane(widget.action)
                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400"
                  : "text-gray-600 dark:text-gray-400"
              } ${widget.action === "createTimesheet" ? "bg-accent-600 text-white shadow-lg" : ""}`}
              aria-label={widget.label}
            >
              {widget.action === "createTimesheet" ? (
                <div className="relative">
                  {renderIcon(widget.icon)}
                  <Plus className="w-3 h-3 absolute -top-1 -right-1 bg-white text-accent-600 rounded-full" />
                </div>
              ) : (
                <>
                  {renderIcon(widget.icon)}
                  {/* Show unread badges for notifications/messages */}
                  {widget.action === "openNotifications" &&
                    unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 bg-error-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                        {unreadNotifications > 9 ? "9+" : unreadNotifications}
                      </span>
                    )}
                  {widget.action === "openMessaging" && unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 bg-error-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                      {unreadMessages > 9 ? "9+" : unreadMessages}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Expandable Panes */}
      {activePane === "ai" && <AIPane onClose={() => setActivePane(null)} />}
      {activePane === "messaging" && (
        <MessagingPane onClose={() => setActivePane(null)} />
      )}
      {activePane === "notifications" && (
        <NotificationsPane onClose={() => setActivePane(null)} />
      )}
    </>
  );
};
